import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input, Select, notification } from "antd";
import { LockOutlined, PhoneOutlined, PauseCircleOutlined, PlayCircleOutlined } from "@ant-design/icons";
import { fetchElevenLabsVoices } from "pages/Sonic/service";
import {
  fillGreetingForPreview,
  speakGreetingPreview,
  stopSpeechPreview,
  unlockAudioPlayback,
} from "pages/Sonic/voicePreview";
import VoiceBrowserTest from "./VoiceBrowserTest";
import { fillTestValues, loadTestValues } from "./voiceTestValues";
import styles from "./style.module.scss";

const PREVIEW_ERROR_KEYS = { unsupported: "sonic.tts_unsupported", api: "sonic.tts_elevenlabs_error" };

/**
 * A voice survey's own voice and brand, and a "Talk in the browser" test of the survey as a
 * customer would hear it on the phone (VoiceBrowserTest, with the survey's unsaved edits).
 * Reuses Sonic's voice list and voice preview.
 */
const PhoneVoiceTest = ({ instrumentId, surveyTitle, questions, phoneCall, language, onChange }) => {
  const { t } = useTranslation();
  const [voices, setVoices] = useState({ loading: true, list: [], defaultId: "", failed: false });
  const [previewing, setPreviewing] = useState(false);
  const voiceId = phoneCall.voiceId || "";
  const brand = (phoneCall.brandName || "").trim();
  const greeting = (phoneCall.greeting || "").trim();

  useEffect(() => {
    let active = true;
    setVoices((v) => ({ ...v, loading: true, failed: false }));
    fetchElevenLabsVoices(language).then((res) => {
      if (!active) return;
      if (res && !res.error && Array.isArray(res.voices)) {
        setVoices({ loading: false, list: res.voices, defaultId: res.default_voice_id || "", failed: false });
      } else {
        setVoices({ loading: false, list: [], defaultId: "", failed: true });
      }
    });
    return () => {
      active = false;
    };
  }, [language]);

  // A voice picked for another call language goes back to that language's default.
  useEffect(() => {
    if (voiceId && voices.list.length && !voices.list.some((v) => v.voice_id === voiceId)) {
      onChange({ voiceId: "" });
    }
  }, [voices.list]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => stopSpeechPreview(), []);

  const listen = () => {
    unlockAudioPlayback();
    if (previewing) {
      stopSpeechPreview();
      setPreviewing(false);
      return;
    }
    const text = greeting
      ? fillGreetingForPreview(fillTestValues(greeting, loadTestValues()), brand || undefined)
      : brand
      ? t("surveyManagement.distribute.phone.voice_sample_brand", { brand })
      : t("surveyManagement.distribute.phone.voice_sample");
    speakGreetingPreview(text, {
      languageCode: language,
      voiceId: voiceId || voices.defaultId,
      voiceEngine: "elevenlabs",
      onStart: () => setPreviewing(true),
      onEnd: () => setPreviewing(false),
      onError: (reason) => {
        setPreviewing(false);
        notification.error({ message: t(PREVIEW_ERROR_KEYS[reason] || "sonic.tts_error") });
      },
    });
  };

  return (
    <div className={styles.phonePanel}>
      <div className={styles.distGrid2}>
        <div className={styles.distField}>
          <label className={styles.fieldLabel}>{t("sonic.brand_name")}</label>
          <Input
            value={phoneCall.brandName}
            maxLength={80}
            placeholder={t("sonic.brand_name_ph")}
            onChange={(e) => onChange({ brandName: e.target.value })}
          />
          <p className={styles.fieldHint}>{t("sonic.brand_name_helper")}</p>
        </div>
        <div className={styles.distField}>
          <label className={styles.fieldLabel}>{t("surveyManagement.distribute.phone.voice")}</label>
          <div className={styles.phoneVoiceRow}>
            <Select
              showSearch
              allowClear
              className={styles.phoneVoiceSelect}
              loading={voices.loading}
              placeholder={t("surveyManagement.distribute.phone.voice_default")}
              value={voiceId || undefined}
              optionFilterProp="label"
              options={voices.list.map((v) => ({ value: v.voice_id, label: v.name }))}
              onChange={(v) => onChange({ voiceId: v || "" })}
            />
            <Button icon={previewing ? <PauseCircleOutlined /> : <PlayCircleOutlined />} onClick={listen}>
              {previewing ? t("sonic.tts_stop") : t("sonic.listen_greeting")}
            </Button>
          </div>
          <p className={styles.fieldHint}>
            {voices.failed
              ? t("surveyManagement.distribute.phone.voice_list_error")
              : t("surveyManagement.distribute.phone.voice_hint")}
          </p>
        </div>
      </div>

      <VoiceBrowserTest
        instrumentId={instrumentId}
        surveyTitle={surveyTitle}
        questions={questions}
        phoneCall={phoneCall}
        language={language}
      >
        <div className={styles.distField}>
          <label className={styles.fieldLabel} htmlFor="voice-test-phone">
            <PhoneOutlined /> {t("surveyManagement.distribute.phone.test_call")}
          </label>
          <div className={`${styles.phoneVoiceRow} ${styles.phoneLocked}`}>
            <Input id="voice-test-phone" disabled placeholder="0532 123 45 67" />
            <Button disabled icon={<PhoneOutlined />}>
              {t("surveyManagement.distribute.phone.test_call_button")}
            </Button>
          </div>
          <p className={styles.fieldHint}>
            <LockOutlined /> {t("surveyManagement.distribute.phone.calls_locked")}
          </p>
        </div>
      </VoiceBrowserTest>
    </div>
  );
};

export default PhoneVoiceTest;
